/**
 * Amounts in «Note spese» are decimal strings with two places («48.50»), as the API and the
 * receipt prefill carry them. Nothing here goes through a floating-point number, so a cent never
 * moves.
 */

const NON_BREAKING_SPACE = '\u00A0';
const CANONICAL = /^(\d+)\.(\d{2})$/;

/**
 * An API amount for display, it-IT style: «48.50» → «48,50 €» (non-breaking space before the
 * sign), thousands with a dot. A value that is not a canonical amount is returned as it came, so
 * a screen never throws on bad data.
 */
export function formatAmount(amount: string): string {
  const negative = amount.startsWith('-');
  const match = CANONICAL.exec(negative ? amount.slice(1) : amount);
  if (match === null) return amount;
  const whole = match[1]!.replace(/^0+(?=\d)/, '').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${negative ? '-' : ''}${whole},${match[2]!}${NON_BREAKING_SPACE}€`;
}

/**
 * What a person typed into an amount field, as an API amount: «48,5» → «48.50». The comma or the
 * dot is the decimal mark (a phone keyboard offers either); when both appear the last one is the
 * decimal mark and the other groups thousands («1.234,5»). A «€» and spaces are ignored. Returns
 * null for anything else: empty, negative, letters, more than two decimals.
 */
export function parseAmountInput(input: string): string | null {
  const text = input.replace(/[\s\u00A0\u20AC]/g, '');
  if (!/^[\d.,]+$/.test(text)) return null;
  const decimalMark = Math.max(text.lastIndexOf(','), text.lastIndexOf('.'));
  const wholeRaw = decimalMark === -1 ? text : text.slice(0, decimalMark);
  const fraction = decimalMark === -1 ? '' : text.slice(decimalMark + 1);
  // A group separator is only valid between digits: no «..», no leading or trailing mark.
  const whole = wholeRaw.replace(/[.,]/g, '');
  if (decimalMark !== -1 && /[.,]/.test(wholeRaw) && !/^\d{1,3}([.,]\d{3})+$/.test(wholeRaw)) {
    return null;
  }
  if (whole === '' && fraction === '') return null;
  if (!/^\d*$/.test(fraction) || fraction.length > 2) return null;
  return `${whole === '' ? '0' : whole.replace(/^0+(?=\d)/, '')}.${fraction.padEnd(2, '0')}`;
}

/**
 * `total` minus each of `parts`, in whole cents through BigInt: the list's total once the rows a
 * pending delete hides are taken out. A value that is not a canonical amount leaves `total` as it
 * came.
 */
export function subtractAmounts(total: string, parts: readonly string[]): string {
  const cents = (amount: string): bigint | null => {
    const negative = amount.startsWith('-');
    const match = CANONICAL.exec(negative ? amount.slice(1) : amount);
    if (match === null) return null;
    const value = BigInt(match[1]!) * 100n + BigInt(match[2]!);
    return negative ? -value : value;
  };
  let left = cents(total);
  if (left === null) return total;
  for (const part of parts) {
    const value = cents(part);
    if (value === null) return total;
    left -= value;
  }
  const negative = left < 0n;
  const abs = negative ? -left : left;
  return `${negative ? '-' : ''}${(abs / 100n).toString()}.${(abs % 100n).toString().padStart(2, '0')}`;
}
